import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import { useSpring, animated, useSpringValue } from "@react-spring/web";
import { ArtworkWithTexture } from "../types";
import ArtworkImageLoader from "./ArtworkImageLoader";

import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { useStore } from "../store/store";

// @ts-ignore
SwiperCore.use([Navigation, Pagination, Autoplay]);

interface ArtworkPickerSwiperProps {
  artworks: ArtworkWithTexture[];
  index: number;
  onSlidedTo: (tags: string[] | null, uuid: string) => void;
  isSelected: boolean;
  endAnimationRunning: boolean;
}

const ArtworkPickerSwiper: React.FC<ArtworkPickerSwiperProps> = ({
  artworks,
  index,
  onSlidedTo,
  isSelected,
  endAnimationRunning,
}) => {
  const { audioManager } = useStore();
  const swiperRef = useRef<SwiperCore>();
  const [activeIndex, setActiveIndex] = useState(0);

  const handleSwiperInit = (swiper: SwiperCore) => {
    swiperRef.current = swiper;
    if (swiperRef.current) {
      setTimeout(() => {
        const randomSlide = Math.floor(
          // @ts-ignore
          Math.random() * swiperRef.current.slides.length - 1,
        );
        swiper.slideTo(randomSlide, 1000);
      }, 1000);
    }
  };

  const handleSlideChange = (swiper: SwiperCore) => {
    setActiveIndex(swiper.realIndex);
    const currentArtwork = artworks[swiper.realIndex];
    const tags = currentArtwork.tags.map((tag) => tag.name.en);
    onSlidedTo(tags, currentArtwork.uuid);
  };

  const slideNext = () => {
    swiperRef.current?.slideNext();
    audioManager!.play("click");
  };

  const slidePrev = () => {
    swiperRef.current?.slidePrev();
    audioManager!.play("click");
  };

  // Animation for the component
  const animationProps = useSpring({
    opacity: 1,
    transform: "translateX(0)",
    from: { opacity: 0, transform: "translateX(200px)" },
    config: { duration: 1200 },
  });

  const fadeOutStyles = useSpring({
    opacity: endAnimationRunning ? 0 : 1,
    config: { duration: 1000 },
  });

  return (
    <animated.div
      className={`flex items-center justify-between h-full w-full relative md:flex-col  swiper-container-${index}`}
      style={animationProps}
    >
      {/* Previous Button */}
      <animated.div
        style={fadeOutStyles}
        className="absolute left-4 flex justify-center z-20 md:top-16 md:left-auto"
      >
        <button
          onClick={slidePrev}
          className="d-icon d-arrowhead-left cursor-pointer opacity-60 hover:opacity-80 text-4xl md:rotate-90 md:text-7xl"
          aria-label="Previous Slide"
        ></button>
      </animated.div>

      {/* Swiper */}
      <Swiper
        onSwiper={handleSwiperInit}
        onSlideChange={handleSlideChange}
        loop
        centeredSlides
        breakpoints={{
          0: {
            slidesPerView: 3,
            direction: "horizontal",
            initialSlide: 5,
          },
          767: {
            spaceBetween: 300,
            direction: "vertical",
            slidesPerView: 3,
            initialSlide: 5,
            simulateTouch: false,
          },
        }}
        initialSlide={3}
        className="flex-grow !md:overflow-visible max-w-sm h-36 md:h-72 z-10 w-full"
      >
        {artworks.map((artwork, idx) => (
          <SwiperSlide
            key={artwork.uuid}
            className="flex items-center justify-center select-none"
          >
            <animated.div
              style={idx !== activeIndex ? fadeOutStyles : {}}
              className="w-full p-2 h-36 md:h-72 md:p-6"
            >
              <ArtworkImageLoader
                className="w-full h-full object-contain"
                artwork={artwork}
                imageWidth={300}
              />
            </animated.div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Next Button */}
      <animated.div
        style={fadeOutStyles}
        className="absolute right-4 flex justify-center z-20 md:bottom-16 md:right-auto"
      >
        <button
          onClick={slideNext}
          className="d-icon d-arrowhead-right cursor-pointer opacity-60 hover:opacity-80 text-4xl md:rotate-90 md:text-7xl "
          aria-label="Next Slide"
        ></button>
      </animated.div>
    </animated.div>
  );
};
export default ArtworkPickerSwiper;
